/**
 * @ordo/i18n — static message catalogs and the ICU formatting runtime.
 *
 * Two consumers, one runtime:
 *
 *   - **Server / pure renderers.** `translatorFor(locale)` returns a
 *     synchronous `(id, values?) => string`. `createIntl()` needs no await and
 *     no init step, which is the property that lets it run inside a pure
 *     HTML-emitting renderer (`javaScriptEnabled: false`, zero fetches).
 *   - **React clients.** `messagesFor(locale)` feeds `IntlProvider`; call sites
 *     use `useIntl()` from `react-intl` directly. This package ships NO JSX and
 *     no React wrapper — re-exporting what `react-intl` already exports is a
 *     one-implementation abstraction.
 *
 * There is NO module-global locale. Two locales can be alive in the same tick,
 * which a server requires and which the retired in-house module got wrong.
 *
 * The locale VOCABULARY (`OrdoLocale`, `toOrdoLocale`) lives in
 * `@ordo/constants/locale`, not here, so `@ordo/schema` and the pure title
 * resolvers can name a locale without pulling in a FormatJS runtime.
 */
import { createIntl, createIntlCache, type IntlShape } from '@formatjs/intl'
import { ORDO_LOCALES, type OrdoLocale, toOrdoLocale } from '@ordo/constants/locale'

import { en, type MessageId } from './messages/en'
import { esUS } from './messages/es-US'

export type { MessageId } from './messages/en'
export { DEFAULT_ORDO_LOCALE, isOrdoLocale, ORDO_LOCALES, type OrdoLocale, toOrdoLocale } from '@ordo/constants/locale'

const MESSAGES: Record<OrdoLocale, Record<MessageId, string>> = {
  'en-US': en,
  'es-US': esUS,
}

/** Messages for one locale, shaped for `<IntlProvider messages={...}>`. */
export function messagesFor(locale: OrdoLocale): Record<MessageId, string> {
  return MESSAGES[locale]
}

/**
 * `formatMessage` does NOT throw on a missing key or a missing interpolation
 * variable — it routes here and returns the dotted id as visible text, which on
 * a parent-facing PDF is worse than an exception. `Record<MessageId, string>`
 * makes that reachable only through a cast, so the type is the real defense;
 * this is the signal for when someone casts anyway.
 *
 * `console` rather than `@ordo/logger`: this package runs in the browser too.
 */
function onError(error: { code?: string; message: string }): void {
  if (error.code === 'MISSING_DATA') {
    // `Intl` locale-data gap. Bun and every supported browser ship full ICU,
    // so this is noise rather than signal.
    return
  }
  console.warn(`[@ordo/i18n] ${error.message}`)
}

// One `IntlShape` per locale, built once at module load. `createIntlCache`
// memoizes the underlying `Intl` formatters, so there is nothing left for a
// lazy cache or a factory to buy.
const cache = createIntlCache()

const INTL: Record<OrdoLocale, IntlShape> = Object.fromEntries(
  ORDO_LOCALES.map((locale) => [locale, createIntl({ locale, messages: MESSAGES[locale], onError }, cache)]),
) as Record<OrdoLocale, IntlShape>

/** The `IntlShape` for a locale — for `RawIntlProvider` or direct formatting. */
export function intlFor(locale: OrdoLocale): IntlShape {
  return INTL[locale]
}

export type Translator = (id: MessageId, values?: Record<string, string | number>) => string

/**
 * A synchronous translator bound to one locale.
 *
 * This is the signature a pure renderer needs: no React, no I/O, no await.
 */
export function translatorFor(locale: OrdoLocale): Translator {
  const intl = INTL[locale]
  return (id, values) => intl.formatMessage({ id }, values)
}

/** Where a browser-side locale choice is persisted. */
export const LOCALE_STORAGE_KEY = 'ordo.locale'

/**
 * Resolve the locale for a React client, in precedence order:
 *
 *   1. an explicit device-level choice in `localStorage`
 *   2. the signed-in user's profile locale
 *   3. `navigator.language`
 *   4. `en-US`
 *
 * Deliberately a PURE function rather than a store with a subscription: pass
 * the profile locale in and let React's own re-render propagate the change
 * when the bootstrap query resolves. A module-global mutable locale is what
 * made the retired in-house module unusable on a server, and a
 * `useSyncExternalStore` wrapper buys nothing a prop does not.
 *
 * Every stored value goes through `toOrdoLocale`, so a poisoned `es_US`
 * already in a user's `localStorage` resolves to `es-US` instead of silently
 * falling back to English.
 */
export function resolveClientLocale(profileLocale?: string | null): OrdoLocale {
  let stored: string | null = null
  try {
    stored = globalThis.localStorage?.getItem(LOCALE_STORAGE_KEY) ?? null
  } catch {
    // Private mode / blocked storage — fall through to the profile.
  }
  if (stored !== null && stored.trim().length > 0) {
    return toOrdoLocale(stored)
  }
  if (typeof profileLocale === 'string' && profileLocale.trim().length > 0) {
    return toOrdoLocale(profileLocale)
  }
  return toOrdoLocale(globalThis.navigator?.language)
}
